import {
  type PropsWithChildren,
  type Dispatch,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  type FilterValue,
  type Filter,
} from "@shopify/hydrogen-react/storefront-api-types";

import { useCollectionAnalytics } from "@react/hooks/useCollectionAnalytics";
import { useCollectionSortOptions } from "@react/hooks/useCollectionSortOptions";
import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { generatePriceFilterValue } from "@react/utilities/collection";
import {
  getAppliedFiltersFromQuery,
  getAppliedSortFromQuery,
  updateQueryFromFilterSortState,
} from "@react/utilities/collectionFilters";
import {
  type PriceRange,
  type SortOption,
  type CollectionSectionSettings,
} from "@tablescape/state";

export type CollectionFiltersContextValues = {
  addFilter: (filter: FilterValue) => void;
  appliedFilters: FilterValue[];
  appliedSort: SortOption | undefined;
  availableFilters: Filter[] | undefined;
  availableSorts: SortOption[];
  clearFilters: () => void;
  collectionPriceMax: number;
  didApplyInitialUrlState: boolean;
  onPriceRangeChange: (range: PriceRange) => void;
  removeFilter: (filter: FilterValue) => void;
  removePriceFilter: () => void;
  setAvailableFilters: Dispatch<SetStateAction<Filter[] | undefined>>;
  setCollectionPriceMax: Dispatch<SetStateAction<number>>;
  updateAppliedSort: (sort: SortOption) => void;
};

const CollectionFiltersContext = createContext<
  CollectionFiltersContextValues | undefined
>(undefined);

type Props = {
  value?: CollectionFiltersContextValues;
};

function CollectionFiltersProvider({
  children,
  value: providedValue,
}: PropsWithChildren<Props>) {
  const { collectionHandle } = useSectionSettings<CollectionSectionSettings>();
  const {
    trackClearAllFilters,
    trackPriceFilterChange,
    trackPriceFilterRemoval,
  } = useCollectionAnalytics();

  const { sortOptions } = useCollectionSortOptions(collectionHandle);

  // Distinguishes URL→state initialization from later user-driven state→URL updates.
  const didApplyInitialUrlState = useRef(false);

  const [availableFilters, setAvailableFilters] = useState<
    Filter[] | undefined
  >(undefined);

  const [appliedFilters, setAppliedFilters] = useState<FilterValue[]>([]);

  const [availableSorts, setAvailableSorts] = useState<SortOption[]>(
    sortOptions ?? []
  );

  const [appliedSort, setAppliedSort] = useState<SortOption | undefined>(
    sortOptions?.[0] ?? undefined
  );

  const [collectionPriceMax, setCollectionPriceMax] = useState<number>(0);

  useEffect(() => {
    if (sortOptions) {
      setAvailableSorts(sortOptions);
      setAppliedSort(sortOptions[0]);
    }
  }, [sortOptions]);

  useEffect(() => {
    // One-time only URL→state initialization
    if (!didApplyInitialUrlState.current && availableFilters) {
      const preservedFilters = getAppliedFiltersFromQuery(availableFilters);
      if (preservedFilters.length > 0) setAppliedFilters(preservedFilters);

      const preservedSort = getAppliedSortFromQuery(availableSorts);
      if (preservedSort) setAppliedSort(preservedSort);

      didApplyInitialUrlState.current = true;
    }
  }, [availableFilters, availableSorts]);

  useEffect(() => {
    // User-driven state→URL updates
    if (didApplyInitialUrlState.current) {
      updateQueryFromFilterSortState({
        appliedFilters,
        appliedSort,
        availableFilters,
        availableSorts,
      });
    }
  }, [appliedFilters, availableFilters, availableSorts, appliedSort]);

  const addFilter = useCallback((filter: FilterValue) => {
    setAppliedFilters((prev) => [...prev, filter]);
  }, []);

  const removeFilter = useCallback((filter: FilterValue) => {
    setAppliedFilters((prev) => prev.filter((f) => f.id !== filter.id));
  }, []);

  const removePriceFilter = useCallback(() => {
    // Track price filter removal event
    const currentPriceFilter = appliedFilters.find(
      (f) => f.id === COLLECTION_FILTER_IDS.PRICE
    );

    if (currentPriceFilter) {
      // Get previous filter values (excluding current price filter)
      const previousFilterValues = appliedFilters
        .filter((f) => f.id !== COLLECTION_FILTER_IDS.PRICE)
        .map((f) => f.label);

      // Parse the price range from the current filter
      const parsedInput = JSON.parse(currentPriceFilter.input as string);
      const priceRange: PriceRange = {
        min: parsedInput.price.min,
        max: parsedInput.price.max,
      };

      trackPriceFilterRemoval(priceRange, previousFilterValues);
    }

    setAppliedFilters((prev) =>
      prev.filter((f) => f.id !== COLLECTION_FILTER_IDS.PRICE)
    );
  }, [appliedFilters, trackPriceFilterRemoval]);

  const clearFilters = useCallback(() => {
    // Track clear all filter events for each applied filter
    if (appliedFilters.length > 0) {
      const previousFilterValues = appliedFilters.map((f) => f.label);
      const clearedFilters = appliedFilters.map((filter) => {
        // Find the filter category for this filter value
        const filterCategory =
          availableFilters?.find((f) =>
            f.values.some((v) => v.id === filter.id)
          )?.label || "Unknown";

        return {
          type: filterCategory,
          value: filter.label,
        };
      });

      trackClearAllFilters(clearedFilters, previousFilterValues);
    }

    setAppliedFilters([]);
  }, [appliedFilters, availableFilters, trackClearAllFilters]);

  const onPriceRangeChange = useCallback(
    (range: PriceRange) => {
      // Get previous filter values before any state changes
      const previousFilterValues = appliedFilters
        .filter((f) => f.id !== COLLECTION_FILTER_IDS.PRICE)
        .map((f) => f.label);

      // Track price filter change event using centralized hook
      trackPriceFilterChange(range, previousFilterValues);

      // Use single state update to avoid race conditions (from main)
      setAppliedFilters((prev) => {
        // Remove existing price filter and add new one in one update
        const withoutPrice = prev.filter(
          (f) => f.id !== COLLECTION_FILTER_IDS.PRICE
        );
        return [
          ...withoutPrice,
          generatePriceFilterValue(range.min, range.max ?? 0),
        ];
      });
    },
    [appliedFilters, trackPriceFilterChange] // generatePriceFilterValue is a stable function reference
  );

  const updateAppliedSort = useCallback((sort: SortOption) => {
    setAppliedSort(sort);
  }, []);

  const value = useMemo(
    () => ({
      ...providedValue,
      addFilter,
      appliedFilters,
      availableFilters,
      appliedSort,
      removeFilter,
      clearFilters,
      availableSorts,
      didApplyInitialUrlState: didApplyInitialUrlState.current,
      collectionPriceMax,
      setCollectionPriceMax,
      setAvailableFilters,
      updateAppliedSort,
      onPriceRangeChange,
      removePriceFilter,
    }),
    [
      providedValue,
      addFilter,
      appliedFilters,
      availableFilters,
      appliedSort,
      removeFilter,
      clearFilters,
      availableSorts,
      didApplyInitialUrlState,
      collectionPriceMax,
      setCollectionPriceMax,
      updateAppliedSort,
      onPriceRangeChange,
      removePriceFilter,
    ]
  );

  return (
    <CollectionFiltersContext.Provider value={value}>
      {children}
    </CollectionFiltersContext.Provider>
  );
}

function useCollectionFilters() {
  const context = useContext<CollectionFiltersContextValues | undefined>(
    CollectionFiltersContext
  );

  if (context === undefined) {
    throw new Error(
      "useCollectionFilters must be used within a CollectionFiltersProvider"
    );
  }

  return context;
}

export {
  CollectionFiltersContext,
  CollectionFiltersProvider,
  useCollectionFilters,
};
